import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';
import { ZagButton } from '../button/button';
import { HelpStepsComponent, type HelpDialogStep } from './help-steps.component';

export { type HelpDialogStep } from './help-steps.component';

/**
 * Data passed to {@link HelpDialogComponent} when opened.
 */
export interface HelpDialogData {
  /** i18n key for the dialog title (e.g. provider name). */
  titleKey: string;
  /** Optional Material icon name shown next to the title. */
  icon?: string;
  /** Ordered list of steps. */
  steps: HelpDialogStep[];
}

/**
 * Stepped help dialog. Provides the chrome (header, progress dots, navigation
 * buttons) around the canonical {@link HelpStepsComponent} which owns body
 * rendering, URL substitution, and copy buttons. The same `HelpStepsComponent`
 * is reused inline inside the AI chat in list mode — no copy-paste.
 *
 * @example
 * ```typescript
 * dialog.open(HelpDialogComponent, {
 *   data: {
 *     titleKey: 'helpDialog.socialLogin.microsoft.title',
 *     icon: 'help_outline',
 *     steps: [
 *       { titleKey: 'helpDialog.socialLogin.microsoft.s1.title', bodyKey: 'helpDialog.socialLogin.microsoft.s1.body' },
 *       // ...
 *     ],
 *   },
 *   panelClass: 'ai-chat-dialog-panel',
 *   backdropClass: 'ai-chat-dialog-backdrop',
 * });
 * ```
 */
@Component({
  selector: 'zag-help-dialog',
  imports: [TranslatePipe, ZagIcon, ZagButton, HelpStepsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-help-dialog-host',
    '(document:keydown.escape)': 'onClose()',
  },
  template: `
    <div class="zag-help-dialog" role="dialog" aria-modal="true" [attr.aria-labelledby]="titleId">
      <header class="zag-help-dialog__header">
        <h2 [id]="titleId" class="zag-help-dialog__title">
          @if (data.icon) {
            <span [zagIcon]="data.icon" class="zag-help-dialog__title-icon"></span>
          }
          {{ data.titleKey | translate }}
        </h2>
        <button type="button" class="zag-help-dialog__close" [attr.aria-label]="'helpDialog.close' | translate" (click)="onClose()">
          <span zagIcon="close"></span>
        </button>
      </header>

      <div class="zag-help-dialog__progress">
        <span class="zag-help-dialog__progress-text">
          {{ 'helpDialog.stepIndicator' | translate: { current: currentIndex() + 1, total: stepCount() } }}
        </span>
        <ol class="zag-help-dialog__dots" role="tablist" [attr.aria-label]="'helpDialog.stepsAriaLabel' | translate">
          @for (step of data.steps; let i = $index; track i) {
            <li>
              <button
                type="button"
                role="tab"
                class="zag-help-dialog__dot"
                [class.zag-help-dialog__dot--active]="i === currentIndex()"
                [class.zag-help-dialog__dot--completed]="i < currentIndex()"
                [attr.aria-selected]="i === currentIndex()"
                [attr.aria-label]="'helpDialog.stepIndicator' | translate: { current: i + 1, total: stepCount() }"
                (click)="goTo(i)"
              ></button>
            </li>
          }
        </ol>
      </div>

      <section class="zag-help-dialog__body" tabindex="0">
        <zag-help-steps mode="stepper" [steps]="data.steps" [(currentIndex)]="currentIndex" />
      </section>

      <footer class="zag-help-dialog__actions">
        <button type="button" zagButton="outlined" zagType="secondary" [disabled]="isFirst()" (click)="previous()">
          <span zagIcon="chevron-left"></span>
          {{ 'helpDialog.previous' | translate }}
        </button>
        @if (isLast()) {
          <button type="button" zagButton zagType="primary" (click)="onClose()">
            {{ 'helpDialog.done' | translate }}
          </button>
        } @else {
          <button type="button" zagButton zagType="primary" (click)="next()">
            {{ 'helpDialog.next' | translate }}
            <span zagIcon="chevron-right" zagEnd></span>
          </button>
        }
      </footer>
    </div>
  `,
  styles: [
    `
      .zag-help-dialog {
        display: flex;
        flex-direction: column;
        width: min(640px, 92vw);
        max-height: 88vh;
        background: var(--surface-color-default, var(--background-color-elevated));
        border-radius: var(--border-radius-lg);
        box-shadow: var(--shadow-lg);
        overflow: hidden;
      }

      .zag-help-dialog__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-md);
        padding: var(--spacing-lg) var(--spacing-lg) var(--spacing-md);
        border-bottom: 1px solid var(--border-color-default, var(--border-color));
      }

      .zag-help-dialog__title {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin: 0;
        font-size: var(--font-size-lg);
        font-weight: var(--font-weight-semibold);
        color: var(--text-color-primary);
      }

      .zag-help-dialog__title-icon {
        color: var(--color-primary, var(--primary-color));
      }

      .zag-help-dialog__close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        background: transparent;
        color: var(--text-color-secondary);
        cursor: pointer;
        border-radius: var(--border-radius-sm);
        transition: background-color 0.15s;
      }

      .zag-help-dialog__close:hover,
      .zag-help-dialog__close:focus-visible {
        background: var(--surface-color-hover, var(--background-color-hover));
        outline: none;
      }

      .zag-help-dialog__progress {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-md);
        padding: var(--spacing-md) var(--spacing-lg);
        background: var(--surface-color-subtle, var(--background-color-subtle));
      }

      .zag-help-dialog__progress-text {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        font-variant-numeric: tabular-nums;
      }

      .zag-help-dialog__dots {
        display: flex;
        gap: var(--spacing-xs, 4px);
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .zag-help-dialog__dot {
        width: 10px;
        height: 10px;
        padding: 0;
        border: none;
        border-radius: 50%;
        background: var(--border-color-default, var(--border-color));
        cursor: pointer;
        transition:
          background-color 0.15s,
          transform 0.15s;
      }

      .zag-help-dialog__dot:hover,
      .zag-help-dialog__dot:focus-visible {
        transform: scale(1.2);
        outline: none;
      }

      .zag-help-dialog__dot--completed {
        background: color-mix(in srgb, var(--color-primary, var(--primary-color)) 60%, transparent);
      }

      .zag-help-dialog__dot--active {
        background: var(--color-primary, var(--primary-color));
        transform: scale(1.2);
      }

      .zag-help-dialog__body {
        flex: 1;
        overflow-y: auto;
        padding: var(--spacing-lg);
      }

      .zag-help-dialog__body:focus-visible {
        outline: none;
      }

      .zag-help-dialog__actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-sm);
        padding: var(--spacing-md) var(--spacing-lg);
        border-top: 1px solid var(--border-color-default, var(--border-color));
      }

      @media (max-width: 480px) {
        .zag-help-dialog__header,
        .zag-help-dialog__progress,
        .zag-help-dialog__body,
        .zag-help-dialog__actions {
          padding-left: var(--spacing-md);
          padding-right: var(--spacing-md);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .zag-help-dialog__dot,
        .zag-help-dialog__close {
          transition: none;
        }
      }
    `,
  ],
})
export class HelpDialogComponent {
  private readonly dialogRef = inject(DialogRef<void>);
  protected readonly data = inject<HelpDialogData>(DIALOG_DATA);
  protected readonly titleId = `zag-help-dialog-title-${nextDialogId()}`;

  protected readonly currentIndex = signal(0);
  protected readonly stepCount = computed(() => this.data.steps.length);
  protected readonly isFirst = computed(() => this.currentIndex() === 0);
  protected readonly isLast = computed(() => this.currentIndex() === this.stepCount() - 1);

  protected next(): void {
    if (this.isLast()) return;
    this.currentIndex.update(i => i + 1);
  }

  protected previous(): void {
    if (this.isFirst()) return;
    this.currentIndex.update(i => i - 1);
  }

  protected goTo(index: number): void {
    if (index < 0 || index >= this.stepCount()) return;
    this.currentIndex.set(index);
  }

  protected onClose(): void {
    this.dialogRef.close();
  }
}

let dialogIdCounter = 0;

function nextDialogId(): number {
  dialogIdCounter += 1;
  return dialogIdCounter;
}
